import { ORDER_TYPE_ENUM, type OrderType } from "@/constants/enums";

export function webReviewsEnabled(platforms: unknown): boolean {
  return Array.isArray(platforms) && platforms.some((value) =>
    typeof value === "string" && (value === "WEB" || value.startsWith("WEB_")));
}

export function ratingSummary(rows: unknown) {
  const values = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.rating))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 5);
  return { count: values.length, average: values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0 };
}

export function orderTypeRoute(type: OrderType, staff: boolean): string {
  if (type === ORDER_TYPE_ENUM.DINE_IN && staff) return "/staff/tables";
  return `/menu?order_type=${type === ORDER_TYPE_ENUM.DELIVERY ? "delivery" : "take-away"}`;
}

const preferenceKey = (tenant: string, staff: boolean) => `zenvix:order-type:${tenant}:${staff ? "staff" : "customer"}`;

/**
 * The preference is mirrored into a cookie so the SERVER can act on it.
 *
 * localStorage is invisible to `getServerSideProps`, so a returning customer was
 * served the landing page and redirected on hydration — the home page painted
 * and was replaced a moment later, which reads as the site blinking. With the
 * cookie the redirect happens before anything is painted.
 */
const ORDER_TYPE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const orderTypeCookieName = (tenant: string, staff: boolean) =>
  `zenvix_order_type_${staff ? "staff" : "customer"}_${tenant.replace(/[^A-Za-z0-9_-]/g, "")}`;

export function readOrderTypeCookie(
  cookies: Partial<Record<string, string>>,
  tenant: string | undefined,
  staff: boolean,
): OrderType | null {
  if (!tenant) return null;
  const value = cookies[orderTypeCookieName(tenant, staff)];
  return Object.values(ORDER_TYPE_ENUM).includes(value as OrderType) ? (value as OrderType) : null;
}

export function rememberOrderType(tenant: string | undefined, staff: boolean, type: OrderType) {
  if (!tenant || typeof window === "undefined") return;
  try { window.localStorage.setItem(preferenceKey(tenant, staff), type); } catch { /* private/offline storage may be unavailable */ }
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${orderTypeCookieName(tenant, staff)}=${type}; Path=/; Max-Age=${ORDER_TYPE_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch { /* cookies may be blocked; the localStorage copy still works */ }
}

export function readOrderType(tenant: string | undefined, staff: boolean): OrderType | null {
  if (!tenant || typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(preferenceKey(tenant, staff));
    return Object.values(ORDER_TYPE_ENUM).includes(value as OrderType) ? value as OrderType : null;
  } catch { return null; }
}
